import { useI18n } from "@/i18n";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon } from "@phosphor-icons/react";

/**
 * Compact toolbar shown top-right on every page.
 * · Theme toggle (Light ↔ Dark)   — cycles between the two most-common modes.
 * · Language switch (ES ↔ EN)     — preserved from previous behavior.
 */
export default function ToolbarToggles({ variant = "light", className = "" }) {
  const { lang, setLang } = useI18n();
  const { isDark, toggle: toggleTheme } = useTheme();
  const isOnDark = variant === "dark" || variant === "onHero";

  const borderCls = isOnDark
    ? "border border-slate-700"
    : "border border-slate-200 dark:border-slate-700";

  const langOn = lang === "en";
  const langTrackOn = isOnDark ? "bg-amber-400/30" : "bg-slate-900";
  const langTrackOff = isOnDark ? "bg-slate-800" : "bg-slate-200";

  // Theme button colors — kept monochrome, sits comfortably next to the lang switch.
  const themeBtnBase = "inline-flex items-center justify-center w-[38px] h-[24px] rounded-full transition-colors cursor-pointer";
  const themeBtnBg = isOnDark
    ? "bg-slate-800 hover:bg-slate-700 text-slate-100"
    : (isDark
        ? "bg-slate-800 hover:bg-slate-700 text-amber-300"
        : "bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-100");

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
        title={isDark ? "Modo claro" : "Modo oscuro"}
        className={`${themeBtnBase} ${borderCls} ${themeBtnBg}`}
        data-testid="toggle-theme"
      >
        {isDark
          ? <Sun size={13} weight="fill" />
          : <Moon size={13} weight="fill" />}
      </button>

      {/* Language toggle */}
      <button
        onClick={() => setLang(lang === "es" ? "en" : "es")}
        role="switch"
        aria-checked={langOn}
        className={`relative inline-flex items-center w-[58px] h-[24px] rounded-full transition-colors cursor-pointer ${borderCls} ${langOn ? langTrackOn : langTrackOff}`}
        data-testid="toggle-lang"
        title="ES / EN"
      >
        <span
          className={`absolute text-[9px] font-bold tracking-wider transition-opacity ${
            langOn
              ? (isOnDark ? "left-2 text-slate-500" : "left-2 text-slate-400")
              : (isOnDark ? "right-2 text-slate-500" : "right-2 text-slate-500")
          }`}
        >
          {langOn ? "ES" : "EN"}
        </span>
        <span
          className={`absolute top-[1px] flex items-center justify-center w-[20px] h-[20px] rounded-full bg-white shadow-sm transition-transform text-[9px] font-bold ${
            langOn ? "translate-x-[36px] text-slate-900" : "translate-x-[1px] text-slate-900"
          }`}
        >
          {langOn ? "EN" : "ES"}
        </span>
      </button>
    </div>
  );
}
